import { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Users,
  User,
  CreditCard,
  AlertTriangle,
  Tag,
  Mail,
  GitPullRequest,
  Clock,
  X,
  Pencil,
  CloudCog,
  Bell,
  GitBranch,
  FolderGit,
  Info,
  Sparkles,
  ExternalLink,
  DollarSign,
} from "lucide-react";
import Layout from "../Layout";
import { RolePill } from "../../components/ui/RolePill";
import { WorkspaceTypeBadge } from "../../components/ui/WorkspaceTypeBadge";
import { EmptyState } from "../../components/ui/EmptyState";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { PageHeader } from "../../components/PageHeader";
import { SectionCard } from "../../components/SectionCard";
import { IconRow } from "../../components/IconRow";
import InviteModal from "../../components/InviteModal";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import TransferOwnershipModal from "../../components/TransferOwnershipModal";
import DeleteWorkspaceModal from "../../components/DeleteWorkspaceModal";
import RenameWorkspaceModal from "../../components/RenameWorkspaceModal";
import BillingPlansModal from "../../components/BillingPlansModal";
import { useToast } from "../../components/ui/toast";
import { TeamsIcon } from "../../components/icons/TeamsIcon";
import { AwsIcon } from "../../components/icons/AwsIcon";
import { SlackIcon } from "../../components/icons/SlackIcon";
import { SiGithub } from "@icons-pack/react-simple-icons";
import { GithubIntegrationSection } from "../../components/GithubIntegrationSection";
import { ConfigureGithubRepoModal } from "../../components/ConfigureGithubRepoModal";

interface Member {
  id: number;
  name: string;
  email: string;
  role: "owner" | "admin" | "member";
  joined_at: string;
  is_current_user: boolean;
  can_make_admin: boolean;
  can_make_member: boolean;
}

interface PendingInvitation {
  id: number;
  email: string;
  role: "owner" | "admin" | "member";
  invited_at: string;
  expires_at: string;
  inviter_name: string;
  days_ago: string;
}

interface AwsAccount {
  id: number;
  uuid: string;
  display_name: string;
  account_id: string;
  status: "untested" | "connected" | "error";
  total_monthly_savings: number;
}

interface GithubRepository {
  uuid: string;
  owner: string;
  name: string;
  created_at: string;
  total_monthly_savings: number;
}

interface Billing {
  plan: string;
  prs_remaining: number;
  billing_email: string;
  subscription_ends_at: string | null;
  subscription_cancel_at_period_end: boolean;
}

interface Workspace {
  id: number;
  name: string;
  slug: string;
  workspace_type: "personal" | "team";
  user_role: "owner" | "admin" | "member";
  can_edit: boolean;
  can_delete: boolean;
  can_manage_members: boolean;
  can_change_roles: boolean;
  can_leave: boolean;
  can_manage_aws_accounts: boolean;
  member_count: number;
  invitation_count: number;
  members: Member[];
  pending_invitations: PendingInvitation[];
  aws_accounts: AwsAccount[];
  github_repositories: GithubRepository[];
  billing: Billing;
  slack_connected: boolean;
  slack_workspace_name: string | null;
  slack_workspace_url: string | null;
  vcs_integration: {
    platform: string;
    org_name: string | null;
    connected_at: string;
  } | null;
}

interface WorkspaceShowProps {
  workspace: Workspace;
}

export default function WorkspaceShow({ workspace }: WorkspaceShowProps) {
  const { showToast } = useToast();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [showBillingPlansModal, setShowBillingPlansModal] = useState(false);
  const [localWorkspaceName, setLocalWorkspaceName] = useState(workspace.name);
  const [localPendingInvitations, setLocalPendingInvitations] = useState(
    workspace.pending_invitations,
  );
  const [localMembers, setLocalMembers] = useState(workspace.members);

  const [localAwsAccounts] = useState(workspace.aws_accounts);
  const [localGithubRepositories, setLocalGithubRepositories] = useState(workspace.github_repositories);
  const [isConfigureRepoModalOpen, setIsConfigureRepoModalOpen] = useState(false);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    invitationId?: number;
    email?: string;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [removeMemberModal, setRemoveMemberModal] = useState<{
    isOpen: boolean;
    memberId?: number;
    memberName?: string;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [changeRoleModal, setChangeRoleModal] = useState<{
    isOpen: boolean;
    memberId?: number;
    memberName?: string;
    currentRole?: string;
    newRole?: string;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [transferOwnershipModal, setTransferOwnershipModal] = useState(false);

  const [leaveWorkspaceModal, setLeaveWorkspaceModal] = useState<{
    isOpen: boolean;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [convertToTeamModal, setConvertToTeamModal] = useState<{
    isOpen: boolean;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [deleteWorkspaceModal, setDeleteWorkspaceModal] = useState<{
    isOpen: boolean;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [disconnectSlackModal, setDisconnectSlackModal] = useState<{
    isOpen: boolean;
    isLoading?: boolean;
  }>({ isOpen: false });

  const [disconnectGithubModal, setDisconnectGithubModal] = useState<{
    isOpen: boolean;
    isLoading?: boolean;
  }>({ isOpen: false });

  const handleInviteSuccess = (invitation: any) => {
    // Add the new invitation to the local state immediately
    const newInvitation = {
      id: invitation.id,
      email: invitation.email,
      role: invitation.role,
      invited_at: invitation.created_at,
      expires_at:
        invitation.expires_at ||
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      inviter_name:
        workspace.members.find((m) => m.is_current_user)?.name || "You",
      days_ago: "Today",
    };

    setLocalPendingInvitations((prev) => [...prev, newInvitation]);
  };


  const handleRenameSuccess = (newName: string) => {
    setLocalWorkspaceName(newName);
  };

  // Sort members: Owner first, then Admin, then Member (alphabetical within each role)
  const sortedMembers = [...localMembers].sort((a, b) => {
    const roleOrder = { owner: 0, admin: 1, member: 2 };
    const roleComparison = roleOrder[a.role] - roleOrder[b.role];
    if (roleComparison !== 0) return roleComparison;
    return a.name.localeCompare(b.name);
  });

  const handleManageBilling = async (action: "checkout" | "portal") => {
    try {
      const endpoint =
        action === "checkout"
          ? `/workspaces/${workspace.slug}/billing/checkout`
          : `/workspaces/${workspace.slug}/billing/portal`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || "",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create billing session");
      }

      // Redirect to Stripe Checkout or Customer Portal
      window.location.href = data.session_url;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to manage billing";
      showToast("error", message);
    }
  };

  const handleRevokeInvitation = (invitationId: number, email: string) => {
    setConfirmModal({
      isOpen: true,
      invitationId,
      email,
      isLoading: false,
    });
  };

  const handleConfirmRevoke = async () => {
    if (!confirmModal.invitationId || !confirmModal.email) return;

    setConfirmModal((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(
        `/workspaces/${workspace.slug}/invites/${confirmModal.invitationId}/revoke`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
        },
      );

      const result = await response.json();

      if (result.success) {
        // Remove the invitation from local state
        setLocalPendingInvitations((prev) =>
          prev.filter((inv) => inv.id !== confirmModal.invitationId),
        );
        showToast(
          "success",
          result.message || "Invitation revoked successfully",
        );
        setConfirmModal({ isOpen: false });
      } else {
        showToast(
          "error",
          result.errors?.join(", ") || "Failed to revoke invitation",
        );
        setConfirmModal({ isOpen: false });
      }
    } catch (error) {
      console.error("Error revoking invitation:", error);
      showToast("error", "Network error. Please try again.");
      setConfirmModal({ isOpen: false });
    }
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({ isOpen: false });
  };

  const handleRemoveMember = (memberId: number, memberName: string) => {
    setRemoveMemberModal({
      isOpen: true,
      memberId,
      memberName,
      isLoading: false,
    });
  };

  const handleConfirmRemoveMember = async () => {
    if (!removeMemberModal.memberId || !removeMemberModal.memberName) return;

    setRemoveMemberModal((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(
        `/workspaces/${workspace.slug}/members/${removeMemberModal.memberId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
        },
      );

      const result = await response.json();

      if (result.success) {
        // Remove the member from local state
        setLocalMembers((prev) =>
          prev.filter((member) => member.id !== removeMemberModal.memberId),
        );
        showToast("success", result.message || "Member removed successfully");
        setRemoveMemberModal({ isOpen: false });
      } else {
        showToast(
          "error",
          result.errors?.join(", ") || "Failed to remove member",
        );
        setRemoveMemberModal({ isOpen: false });
      }
    } catch (error) {
      console.error("Error removing member:", error);
      showToast("error", "Network error. Please try again.");
      setRemoveMemberModal({ isOpen: false });
    }
  };

  const handleCloseRemoveMemberModal = () => {
    setRemoveMemberModal({ isOpen: false });
  };

  const handleChangeRole = (
    memberId: number,
    memberName: string,
    currentRole: string,
    newRole: string,
  ) => {
    setChangeRoleModal({
      isOpen: true,
      memberId,
      memberName,
      currentRole,
      newRole,
      isLoading: false,
    });
  };

  const handleConfirmChangeRole = async () => {
    if (!changeRoleModal.memberId || !changeRoleModal.newRole) return;

    setChangeRoleModal((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(
        `/workspaces/${workspace.slug}/members/${changeRoleModal.memberId}/role`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify({ role: changeRoleModal.newRole }),
        },
      );

      const result = await response.json();

      if (result.success) {
        // Update the member's role in local state and recalculate permissions
        setLocalMembers((prev) =>
          prev.map((member) => {
            if (member.id === changeRoleModal.memberId) {
              const updatedMember = {
                ...member,
                role: changeRoleModal.newRole as "owner" | "admin" | "member",
              };
              const permissions = calculateMemberPermissions(updatedMember);
              return {
                ...updatedMember,
                ...permissions,
              };
            }
            return member;
          }),
        );
        showToast(
          "success",
          result.message || "Member role changed successfully",
        );
        setChangeRoleModal({ isOpen: false });
      } else {
        showToast(
          "error",
          result.errors?.join(", ") || "Failed to change member role",
        );
        setChangeRoleModal({ isOpen: false });
      }
    } catch (error) {
      console.error("Error changing member role:", error);
      showToast("error", "Network error. Please try again.");
      setChangeRoleModal({ isOpen: false });
    }
  };

  const handleCloseChangeRoleModal = () => {
    setChangeRoleModal({ isOpen: false });
  };

  const handleTransferOwnershipSuccess = (targetEmail: string) => {
    // Update local state - current user becomes admin, target user becomes owner
    const currentUser = localMembers.find((m) => m.is_current_user);
    const targetUser = localMembers.find((m) => m.email === targetEmail);

    if (currentUser && targetUser) {
      setLocalMembers((prev) => {
        // First, update roles
        const updatedMembers = prev.map((member) => {
          if (member.is_current_user) {
            return { ...member, role: "admin" as const };
          }
          if (member.email === targetEmail) {
            return { ...member, role: "owner" as const };
          }
          return member;
        });

        // Then recalculate permissions for all members based on new roles
        return updatedMembers.map((member) => {
          const currentUserRole =
            updatedMembers.find((m) => m.is_current_user)?.role ||
            workspace.user_role;
          const permissions = calculateMemberPermissionsWithRole(
            member,
            currentUserRole,
          );
          return {
            ...member,
            ...permissions,
          };
        });
      });
    }
  };

  // Helper function to calculate member permissions with explicit current user role
  const calculateMemberPermissionsWithRole = (
    member: Member,
    currentUserRole: string,
  ) => {
    // Can't change your own role
    if (member.is_current_user) {
      return {
        can_make_admin: false,
        can_make_member: false,
      };
    }

    // Must have permission to change roles
    if (!workspace.can_change_roles) {
      return {
        can_make_admin: false,
        can_make_member: false,
      };
    }

    const targetRole = member.role;

    // No one can change owner roles except owners themselves
    if (targetRole === "owner" && currentUserRole !== "owner") {
      return {
        can_make_admin: false,
        can_make_member: false,
      };
    }

    // Admins can only promote members to admin (not demote other admins)
    if (currentUserRole === "admin") {
      return {
        can_make_admin: targetRole === "member", // Can only promote members
        can_make_member: false, // Can't demote anyone
      };
    }

    // Owners can change anyone (except to show buttons only when role would actually change)
    if (currentUserRole === "owner") {
      return {
        can_make_admin: targetRole !== "admin", // Don't show if already admin
        can_make_member: targetRole !== "member", // Don't show if already member
      };
    }

    // Default: no permissions
    return {
      can_make_admin: false,
      can_make_member: false,
    };
  };

  // Helper function to calculate member permissions (uses current local state)
  const calculateMemberPermissions = (member: Member) => {
    const currentUserRole =
      localMembers.find((m) => m.is_current_user)?.role || workspace.user_role;
    return calculateMemberPermissionsWithRole(member, currentUserRole);
  };

  const handleLeaveWorkspace = () => {
    setLeaveWorkspaceModal({
      isOpen: true,
      isLoading: false,
    });
  };

  const handleConfirmLeaveWorkspace = async () => {
    setLeaveWorkspaceModal((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(`/workspaces/${workspace.slug}/leave`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });

      const result = await response.json();

      if (result.success) {
        // Flash message will show as toast on destination page
        router.visit("/workspaces");
      } else {
        showToast("error", result.errors?.join(", ") || "Failed to leave workspace. Please try again.");
        setLeaveWorkspaceModal({ isOpen: false });
      }
    } catch (error) {
      console.error("Error leaving workspace:", error);
      showToast("error", "Network error. Please try again.");
      setLeaveWorkspaceModal({ isOpen: false });
    }
  };

  const handleCloseLeaveWorkspaceModal = () => {
    setLeaveWorkspaceModal({ isOpen: false });
  };

  const handleConvertToTeam = () => {
    setConvertToTeamModal({
      isOpen: true,
      isLoading: false,
    });
  };

  const handleConfirmConvertToTeam = async () => {
    setConvertToTeamModal((prev) => ({ ...prev, isLoading: true }));

    const form = document.createElement("form");
    form.method = "POST";
    form.action = `/workspaces/${workspace.slug}/convert_to_team`;

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "PATCH";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "authenticity_token";
    tokenInput.value =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || "";

    form.appendChild(methodInput);
    form.appendChild(tokenInput);
    document.body.appendChild(form);
    form.submit();
  };

  const handleCloseConvertToTeamModal = () => {
    setConvertToTeamModal({ isOpen: false });
  };

  const handleDelete = () => {
    setDeleteWorkspaceModal({ isOpen: true, isLoading: false });
  };

  const handleConfirmDelete = async () => {
    setDeleteWorkspaceModal((prev) => ({ ...prev, isLoading: true }));

    const form = document.createElement("form");
    form.method = "POST";
    form.action = `/workspaces/${workspace.slug}`;

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "DELETE";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "authenticity_token";
    tokenInput.value =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || "";

    form.appendChild(methodInput);
    form.appendChild(tokenInput);
    document.body.appendChild(form);
    form.submit();
  };

  const handleCloseDeleteModal = () => {
    setDeleteWorkspaceModal({ isOpen: false });
  };

  const handleDisconnectSlack = () => {
    setDisconnectSlackModal({ isOpen: true, isLoading: false });
  };

  const handleConfirmDisconnectSlack = async () => {
    setDisconnectSlackModal((prev) => ({ ...prev, isLoading: true }));

    const form = document.createElement("form");
    form.method = "POST";
    form.action = `/workspaces/${workspace.slug}/slack/disconnect`;

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "DELETE";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "authenticity_token";
    tokenInput.value =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || "";

    form.appendChild(methodInput);
    form.appendChild(tokenInput);
    document.body.appendChild(form);
    form.submit();
  };

  const handleCloseDisconnectSlackModal = () => {
    setDisconnectSlackModal({ isOpen: false });
  };

  const handleConfirmDisconnectGithub = async () => {
    setDisconnectGithubModal((prev) => ({ ...prev, isLoading: true }));

    const form = document.createElement("form");
    form.method = "POST";
    form.action = `/workspaces/${workspace.slug}/vcs_integrations/github/disconnect`;

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "DELETE";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "authenticity_token";
    tokenInput.value =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || "";

    form.appendChild(methodInput);
    form.appendChild(tokenInput);
    document.body.appendChild(form);
    form.submit();
  };

  const handleCloseDisconnectGithubModal = () => {
    setDisconnectGithubModal({ isOpen: false });
  };

  return (
    <Layout
      title={localWorkspaceName}
      description={`${workspace.workspace_type} workspace`}
    >
      <section className="px-4 sm:px-6 lg:px-8 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation
            items={[
              { label: "Workspaces", href: "/workspaces" },
              {
                label: localWorkspaceName,
              },
            ]}
          />

          <PageHeader
            title={localWorkspaceName}
            badges={[
              <WorkspaceTypeBadge key="type" type={workspace.workspace_type} />,
              <RolePill key="role" role={workspace.user_role} />,
            ]}
            actions={
              workspace.can_edit && (
                <button
                  onClick={() => setIsRenameModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Rename
                </button>
              )
            }
          />

          {/* AWS Accounts Section */}
          <SectionCard
            title="Cloud accounts"
            icon={CloudCog}
            className="mb-6"
            action={
              workspace.can_manage_aws_accounts && localAwsAccounts.length > 0 ? (
                <a
                  href={`/workspaces/${workspace.slug}/aws_accounts/new`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <CloudCog className="w-3.5 h-3.5" />
                  Add account
                </a>
              ) : undefined
            }
          >
            {localAwsAccounts.length > 0 ? (
              <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
                {localAwsAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <AwsIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {account.display_name}
                        </h3>
                        <code className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-transparent">
                          {account.account_id}
                        </code>
                        {account.total_monthly_savings > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                            ${account.total_monthly_savings.toFixed(2)}/mo
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <a
                        href={`/workspaces/${workspace.slug}/aws_accounts/${account.uuid}`}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={CloudCog}
                title="No cloud accounts yet"
                description="Connect your cloud accounts to start analyzing your infrastructure and receive cost optimization recommendations."
                actionText="Add account"
                actionIcon={CloudCog}
                actionUrl={`/workspaces/${workspace.slug}/aws_accounts/new`}
              />
            )}
          </SectionCard>

          {/* Infrastructure Repositories Section */}
          <SectionCard
            title="Infrastructure repositories"
            icon={FolderGit}
            className="mb-6"
            action={
              workspace.can_edit && workspace.vcs_integration && localGithubRepositories.length > 0 ? (
                <button
                  onClick={() => setIsConfigureRepoModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <FolderGit className="w-3.5 h-3.5" />
                  Add repository
                </button>
              ) : undefined
            }
          >
            {localGithubRepositories.length > 0 ? (
              <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
                {localGithubRepositories.map((repo) => (
                  <div
                    key={repo.uuid}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <SiGithub className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {repo.owner}/{repo.name}
                        </h3>
                        {repo.total_monthly_savings > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                            ${repo.total_monthly_savings.toFixed(2)}/mo
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <a
                        href={`/workspaces/${workspace.slug}/github_repositories/${repo.uuid}`}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : workspace.vcs_integration ? (
              <EmptyState
                icon={FolderGit}
                title="No infrastructure repositories yet"
                description="Add your infrastructure repositories to start creating pull requests with cost optimization recommendations."
                actionText="Add repository"
                actionIcon={FolderGit}
                onAction={() => setIsConfigureRepoModalOpen(true)}
              />
            ) : (
              <EmptyState
                icon={FolderGit}
                title="Connect GitHub to add repositories"
                description="Connect your GitHub organization to manage infrastructure repositories and create pull requests."
                actionText="Connect GitHub"
                actionIcon={SiGithub}
                actionUrl="#vcs-integrations"
              />
            )}
          </SectionCard>

          {/* VCS Integrations Section */}
          <SectionCard
            title="VCS Integrations"
            icon={GitBranch}
            className="mb-6"
            id="vcs-integrations"
          >
            <GithubIntegrationSection
              vcsIntegration={workspace.vcs_integration}
              canEdit={workspace.can_edit}
              workspaceSlug={workspace.slug}
              onDisconnect={() => setDisconnectGithubModal({ isOpen: true })}
            />
          </SectionCard>

          {/* Notification Integrations Section */}
          <SectionCard
            title="Notification integrations"
            icon={Bell}
            className="mb-6"
          >
            <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Slack Subsection */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2.5">
                  <SlackIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Slack
                    </h3>
                    {workspace.slack_connected ? (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Connected to{" "}
                        <a
                          href={workspace.slack_workspace_url || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary hover:underline"
                        >
                          {workspace.slack_workspace_name}
                        </a>
                      </p>
                    ) : (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Send recommendations and PR updates to Slack
                      </p>
                    )}
                  </div>
                </div>
                {workspace.slack_connected ? (
                  <button
                    onClick={handleDisconnectSlack}
                    disabled={!workspace.can_edit}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Disconnect
                  </button>
                ) : (
                  <a
                    href={`/workspaces/${workspace.slug}/slack/oauth`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <SlackIcon className="w-3.5 h-3.5" />
                    Add to Slack
                  </a>
                )}
              </div>

              {/* Microsoft Teams Subsection */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2.5">
                  <TeamsIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Microsoft Teams
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Send recommendations and PR updates to Teams
                    </p>
                  </div>
                </div>
                <button
                  disabled
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium rounded-full cursor-not-allowed whitespace-nowrap"
                >
                  Coming soon
                </button>
              </div>
            </div>
          </SectionCard>

          {/* Members & Invites Section */}
          {workspace.workspace_type === "team" && (
            <SectionCard
              title="Members"
              icon={Users}
              className="mb-6"
              badge={
                workspace.invitation_count > 0 ? (
                  <span className="text-xs text-surface-foreground/50">
                    {workspace.invitation_count} pending
                  </span>
                ) : undefined
              }
              footer={`${localMembers.length} ${localMembers.length === 1 ? "member" : "members"}`}
              action={
                workspace.can_manage_members ? (
                  <button
                    onClick={() => setIsInviteModalOpen(true)}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <Users className="w-3.5 h-3.5" />
                    Invite
                  </button>
                ) : undefined
              }
            >
              <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedMembers.map((member) => (
                  <div key={member.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <User className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              <span title={member.name}>{member.name}</span>
                              <span
                                className="font-normal text-xs text-gray-500 dark:text-gray-400 ml-1"
                                title={member.email}
                              >
                                ({member.email})
                              </span>
                              {member.is_current_user && (
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                                  (you)
                                </span>
                              )}
                            </p>
                            <RolePill role={member.role} />
                          </div>
                        </div>
                      </div>

                      {/* Right side: Actions */}
                      {workspace.can_manage_members &&
                        !member.is_current_user && (
                          <div className="flex gap-2 ml-4">
                            {/* Make Admin Button */}
                            {member.can_make_admin && (
                              <button
                                onClick={() =>
                                  handleChangeRole(
                                    member.id,
                                    member.name,
                                    member.role,
                                    "admin",
                                  )
                                }
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                                aria-label={`Make ${member.name} admin`}
                              >
                                Make admin
                              </button>
                            )}

                            {/* Make Member Button */}
                            {member.can_make_member && (
                              <button
                                onClick={() =>
                                  handleChangeRole(
                                    member.id,
                                    member.name,
                                    member.role,
                                    "member",
                                  )
                                }
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                                aria-label={`Make ${member.name} member`}
                              >
                                Make member
                              </button>
                            )}
                            {/* Show remove button only if current user is owner OR target member is not owner */}
                            {(() => {
                              const currentUserRole =
                                localMembers.find((m) => m.is_current_user)
                                  ?.role || workspace.user_role;
                              return (
                                currentUserRole === "owner" ||
                                member.role !== "owner"
                              );
                            })() && (
                              <button
                                onClick={() =>
                                  handleRemoveMember(member.id, member.name)
                                }
                                className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-full text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                                aria-label={`Remove ${member.name}`}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pending Invitations Section */}
              {localPendingInvitations.length > 0 &&
                workspace.can_manage_members && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Pending invitations
                    </h3>
                    <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
                      {localPendingInvitations.map((invitation) => (
                        <div key={invitation.id} className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    <span title={invitation.email}>
                                      {invitation.email}
                                    </span>
                                  </p>
                                  <RolePill role={invitation.role} />
                                </div>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Clock className="w-3 h-3 text-gray-400" />
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Invited {invitation.days_ago.toLowerCase()}{" "}
                                    by {invitation.inviter_name}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Right side: Revoke button */}
                            <button
                              onClick={() =>
                                handleRevokeInvitation(
                                  invitation.id,
                                  invitation.email,
                                )
                              }
                              className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-full text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer ml-4"
                              aria-label={`Revoke invitation for ${invitation.email}`}
                            >
                              <X className="w-3 h-3 mr-1" />
                              Revoke
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </SectionCard>
          )}

          <SectionCard
            title="Billing"
            icon={CreditCard}
            action={
              workspace.billing.plan === "free" ? (
                <button
                  onClick={() => setShowBillingPlansModal(true)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full text-white bg-primary hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  View plans
                </button>
              ) : (
                <button
                  onClick={() => handleManageBilling("portal")}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Manage
                </button>
              )
            }
            className="mb-6"
            footer={
              (workspace.billing.plan === "pro" && workspace.billing.subscription_ends_at) ? (
                <div className="flex items-center gap-1.5">
                  <Info className={`w-3.5 h-3.5 flex-shrink-0 ${
                    workspace.billing.subscription_cancel_at_period_end
                      ? "text-red-500"
                      : "text-primary"
                  }`} />
                  <span>
                    {workspace.billing.subscription_cancel_at_period_end
                      ? `Ends ${new Date(workspace.billing.subscription_ends_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}. Will not auto-renew.`
                      : `Auto-renews ${new Date(workspace.billing.subscription_ends_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`
                    }
                  </span>
                </div>
              ) : (workspace.billing.plan === "free" && workspace.billing.prs_remaining === 0) ? (
                <span>Out of free PRs? Subscribe for unlimited PR generation.</span>
              ) : undefined
            }
          >
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <IconRow icon={Tag}>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                  workspace.billing.plan === "pro"
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                }`}>
                  {workspace.billing.plan}
                </span>
              </IconRow>

              {workspace.billing.plan === "pro" && (
                <IconRow icon={DollarSign}>
                  <span className="font-semibold">$99/month</span>
                </IconRow>
              )}

              <IconRow icon={GitPullRequest}>
                {workspace.billing.plan === "free" ? (
                  <>
                    <span className="font-semibold">{workspace.billing.prs_remaining}</span>
                    <span className="text-gray-500 dark:text-gray-400"> / 3 remaining</span>
                  </>
                ) : (
                  <span className="font-semibold">Unlimited</span>
                )}
              </IconRow>
            </div>
          </SectionCard>

          {/* Danger Zone */}
          <SectionCard title="Danger zone" icon={AlertTriangle} variant="danger">
            <div className="space-y-0 divide-y divide-red-200 dark:divide-red-800">
              {/* Convert to Team (Personal only) */}
              {workspace.workspace_type === "personal" && (
                <div className="flex items-center justify-between py-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Convert to team workspace
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      Make this workspace collaborative.
                    </p>
                  </div>
                  <button
                    onClick={handleConvertToTeam}
                    className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-orange-300 dark:border-orange-600 text-xs font-medium rounded-full text-orange-700 dark:text-orange-300 bg-white dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors cursor-pointer"
                  >
                    Convert
                  </button>
                </div>
              )}

              {/* Transfer Ownership (Team, owner only) */}
              {workspace.workspace_type === "team" &&
                workspace.can_delete &&
                localMembers.filter(
                  (member) =>
                    !member.is_current_user && member.role !== "owner",
                ).length > 0 && (
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Transfer ownership
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        Transfer ownership to another member of this workspace.
                      </p>
                    </div>
                    <button
                      onClick={() => setTransferOwnershipModal(true)}
                      className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-orange-300 dark:border-orange-600 text-xs font-medium rounded-full text-orange-700 dark:text-orange-300 bg-white dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors cursor-pointer"
                    >
                      Transfer
                    </button>
                  </div>
                )}

              {/* Transfer Ownership - Disabled State */}
              {workspace.workspace_type === "team" &&
                workspace.can_delete &&
                workspace.member_count <= 1 && (
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Transfer ownership
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        Invite a teammate to transfer ownership.
                      </p>
                    </div>
                    <button
                      disabled
                      className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 cursor-not-allowed"
                    >
                      Transfer
                    </button>
                  </div>
                )}

              {/* Leave Workspace (non-owner, team workspaces only) */}
              {workspace.can_leave &&
                workspace.user_role !== "owner" &&
                workspace.workspace_type === "team" && (
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Leave workspace
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        You'll lose access to this workspace. You can be
                        re-invited later.
                      </p>
                    </div>
                    <button
                      onClick={handleLeaveWorkspace}
                      className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-full text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                    >
                      Leave
                    </button>
                  </div>
                )}

              {/* Delete Workspace (owner only) */}
              {workspace.can_delete && (
                <div className="flex items-center justify-between py-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Delete workspace
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      Permanently remove this workspace and all member access.
                    </p>
                  </div>
                  <button
                    onClick={handleDelete}
                    className="flex-shrink-0 ml-4 inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-600 text-xs font-medium rounded-full text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </section>

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        workspaceSlug={workspace.slug}
        onInviteSuccess={handleInviteSuccess}
      />

      <BillingPlansModal
        isOpen={showBillingPlansModal}
        onClose={() => setShowBillingPlansModal(false)}
        onSubscribe={() => {
          setShowBillingPlansModal(false);
          handleManageBilling("checkout");
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmRevoke}
        title="Revoke invitation"
        message={`Are you sure you want to revoke the invitation for ${confirmModal.email}? This action cannot be undone.`}
        confirmText="Revoke"
        cancelText="Cancel"
        variant="danger"
        isLoading={confirmModal.isLoading}
      />

      {/* Remove Member Confirmation Modal */}
      <ConfirmationModal
        isOpen={removeMemberModal.isOpen}
        onClose={handleCloseRemoveMemberModal}
        onConfirm={handleConfirmRemoveMember}
        title="Remove member"
        message={`Are you sure you want to remove ${removeMemberModal.memberName} from this workspace? They will lose access to all data in this workspace.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={removeMemberModal.isLoading}
      />

      {/* Change Role Confirmation Modal */}
      <ConfirmationModal
        isOpen={changeRoleModal.isOpen}
        onClose={handleCloseChangeRoleModal}
        onConfirm={handleConfirmChangeRole}
        title="Change member role"
        message={`Are you sure you want to change ${changeRoleModal.memberName}'s role from ${changeRoleModal.currentRole} to ${changeRoleModal.newRole}?`}
        confirmText="Change role"
        cancelText="Cancel"
        variant="default"
        isLoading={changeRoleModal.isLoading}
      />

      <TransferOwnershipModal
        isOpen={transferOwnershipModal}
        onClose={() => setTransferOwnershipModal(false)}
        workspaceSlug={workspace.slug}
        workspaceName={workspace.name}
        members={localMembers}
        onTransferSuccess={handleTransferOwnershipSuccess}
      />

      {/* Leave Workspace Confirmation Modal */}
      <ConfirmationModal
        isOpen={leaveWorkspaceModal.isOpen}
        onClose={handleCloseLeaveWorkspaceModal}
        onConfirm={handleConfirmLeaveWorkspace}
        title="Leave workspace"
        message={`Are you sure you want to leave ${workspace.name}? You'll lose access to all data in this workspace.`}
        confirmText="Leave workspace"
        cancelText="Cancel"
        variant="danger"
        isLoading={leaveWorkspaceModal.isLoading}
      />

      {/* Convert to Team Confirmation Modal */}
      <ConfirmationModal
        isOpen={convertToTeamModal.isOpen}
        onClose={handleCloseConvertToTeamModal}
        onConfirm={handleConfirmConvertToTeam}
        title="Convert to team workspace"
        message="Are you sure you want to convert this workspace to a team workspace? This action cannot be undone."
        confirmText="Convert to team"
        cancelText="Cancel"
        variant="warning"
        isLoading={convertToTeamModal.isLoading}
      />

      {/* Delete Workspace Modal */}
      <DeleteWorkspaceModal
        isOpen={deleteWorkspaceModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        workspaceName={workspace.name}
        isLoading={deleteWorkspaceModal.isLoading}
      />

      {/* Disconnect Slack Modal */}
      <ConfirmationModal
        isOpen={disconnectSlackModal.isOpen}
        onClose={handleCloseDisconnectSlackModal}
        onConfirm={handleConfirmDisconnectSlack}
        title="Disconnect Slack"
        message="Are you sure you want to disconnect Slack? You will no longer receive notifications in your Slack workspace. You will also need to manually remove the Infralyst bot from your Slack workspace."
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
        isLoading={disconnectSlackModal.isLoading}
      />

      {/* Disconnect GitHub Modal */}
      <ConfirmationModal
        isOpen={disconnectGithubModal.isOpen}
        onClose={handleCloseDisconnectGithubModal}
        onConfirm={handleConfirmDisconnectGithub}
        title="Disconnect GitHub"
        message={`Are you sure you want to disconnect GitHub? This will:\n\n• Remove all configured repositories from Infralyst\n• Delete all repository notification settings\n• Prevent you from creating new pull requests\n\nYou will also need to manually remove the Infralyst app from your GitHub organization.`}
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
        isLoading={disconnectGithubModal.isLoading}
      />


      {/* Rename Workspace Modal */}
      <RenameWorkspaceModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        workspaceSlug={workspace.slug}
        workspaceName={localWorkspaceName}
        onRenameSuccess={handleRenameSuccess}
      />

      {/* Configure GitHub Repository Modal */}
      <ConfigureGithubRepoModal
        isOpen={isConfigureRepoModalOpen}
        onClose={() => setIsConfigureRepoModalOpen(false)}
        workspaceSlug={workspace.slug}
        onSuccess={(newRepo) => {
          setLocalGithubRepositories([...localGithubRepositories, newRepo]);
        }}
      />
    </Layout>
  );
}
